import { copyToClipboard } from '@thoughtspot/browser-util';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { getAccessToken, getCurrentOrgId, getScimBaseUrl, getUserPrivileges, isScimPerOrgEnabled, } from '@thoughtspot/session-service';
import { getIdpDomain } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useState } from 'react';
import { LearnMore, LearnMoreConfig, } from "../../../../../system/common/constants";
import { useCreateScimTokenMutation, useGetScimAuthTokenLazyQuery, } from '../../../../../../services/saml/saml-service';
import { TranslationsKeys } from '../../../../../authentication/constants';
import { useProvisioningMutation } from '../../../../../authentication/provisioning/useProvisioningCustomHook';
import styles from './all-orgs-settings-modal.module.scss';
import { LocalAuthContent } from './local-auth-content.container';
const SIDEBAR_TABS = [
    {
        tab: 'scim',
        translationKey: TranslationsKeys.SETTINGS_SIDEBAR_SCIM_PROVISIONING,
        testId: 'sidebar-scim',
    },
    {
        tab: 'localAuth',
        translationKey: TranslationsKeys.SETTINGS_SIDEBAR_LOCAL_AUTH,
        testId: 'sidebar-local-auth',
    },
    {
        tab: 'mfaSettings',
        translationKey: TranslationsKeys.SETTINGS_SIDEBAR_MFA_SETTINGS,
        testId: 'sidebar-mfa-settings',
    },
];
export const AllOrgsSettingsModal = ({ onClose, onSave, initialTab = 'scim', }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [nonce, setNonce] = useState('');
    const [tokenUrl, setTokenUrl] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [urlCopied, setUrlCopied] = useState(false);
    const [tokenCopied, setTokenCopied] = useState(false);
    const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
    const [showRegenerateError, setShowRegenerateError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [settingsChanged, setSettingsChanged] = useState(false);
    const scimBaseUrl = `${getScimBaseUrl()}/scim/v2/`;
    const learnMoreKey = LearnMore.INTERNAL_AUTH;
    const learnMore = LearnMoreConfig[learnMoreKey] || undefined;
    const getDefaultScimHeaders = () => ({
        accessToken: `Bearer ${getAccessToken()}`,
        xTsPrivileges: btoa(JSON.stringify(getUserPrivileges())),
        xTsIdpDomain: getIdpDomain(),
    });
    const { createScimToken, error, data, loading, } = useCreateScimTokenMutation();
    const { fetchSignedToken: createSignedScimToken, error: authError, data: authData, loading: authLoading, } = useProvisioningMutation();
    const { getScimToken, error: getScimTokenError, ScimTokenAuthData, loading: getScimTokenLoading, } = useGetScimAuthTokenLazyQuery();
    const generateSignedToken = useCallback((nonceValue) => {
        createSignedScimToken(window.location.hostname, nonceValue, `${getCurrentOrgId()}`, getDefaultScimHeaders(), isScimPerOrgEnabled(), getScimBaseUrl());
    }, []);
    useEffect(() => {
        if (!getScimTokenLoading && (ScimTokenAuthData || getScimTokenError)) {
            if (ScimTokenAuthData?.token) {
                setShowRegenerateWarning(false);
                setNonce(ScimTokenAuthData?.token);
            }
        }
    }, [ScimTokenAuthData, getScimTokenError, getScimTokenLoading]);
    useEffect(() => {
        if (!loading && (data || error)) {
            if (error) {
                setShowRegenerateError(true);
                setErrorMessage(error?.message);
            }
            else if (data?.Error) {
                setShowRegenerateError(true);
                setErrorMessage(data?.Error);
                setIsToastVisible(false);
                setShowRegenerateWarning(false);
            }
            else {
                setTokenCopied(false);
                setIsToastVisible(true);
                setToastMessage(t(TranslationsKeys.PROVISIONING_GENERATE_SUCCESS));
                setNonce(data?.Data?.token);
                generateSignedToken(data?.Data?.token);
                setSettingsChanged(true);
            }
        }
    }, [data, error, loading]);
    useEffect(() => {
        if (!authLoading && (authData || authError)) {
            setIsLoading(false);
            if (authError) {
            }
            else {
                setTokenUrl(authData?.token);
                setIsLoading(false);
                setIsToastVisible(true);
                setToastMessage(t(TranslationsKeys.PROVISIONING_GENERATE_SUCCESS));
            }
        }
    }, [authData, authError, authLoading]);
    useEffect(() => {
        getScimToken('primary');
    }, []);
    const handleCopyUrl = () => {
        copyToClipboard(scimBaseUrl, () => {
            setToastMessage(t(TranslationsKeys.SETTINGS_SCIM_BASEURL_COPIED_SUCCESS));
            setIsToastVisible(true);
        });
        setUrlCopied(true);
    };
    const handleCopyToken = () => {
        copyToClipboard(tokenUrl, () => {
            setToastMessage(t(TranslationsKeys.PROVISIONING_COPIED_SUCCESS));
            setIsToastVisible(true);
        });
        setTokenCopied(true);
    };
    const handleSave = () => {
        if (settingsChanged) {
            setToastMessage(t(TranslationsKeys.SETTINGS_AUTH_CHANGES_SAVED));
            setIsToastVisible(true);
            setTimeout(onSave, 500);
        }
        else {
            onSave();
        }
    };
    const handleGenerateToken = () => {
        setIsLoading(true);
        if (nonce !== undefined && nonce !== null && nonce.length !== 0) {
            setShowRegenerateWarning(true);
        }
        createScimToken('primary');
    };
    const renderScimContent = () => (React.createElement(Vertical, { className: styles.allOrgsContent },
        React.createElement(Typography, { variant: "h3", className: styles.allOrgsSectionTitle, noMargin: true }, t(TranslationsKeys.PROVISIONING_HEADER)),
        React.createElement("div", { className: styles.allOrgsDescriptionRow },
            React.createElement(Typography, { variant: "body-normal", className: styles.allOrgsDescription, noMargin: true },
                t(TranslationsKeys.SETTINGS_PROVISIONING_HEADING_SUBTITLE),
                ' ',
                React.createElement(Link, { className: styles.allOrgsLearnMore, href: learnMore?.url, target: learnMore?.newWindow ? '_blank' : undefined, rel: "noreferrer", tabIndex: 0 }, t(TranslationsKeys.SECURITY_LEARN_MORE)))),
        React.createElement(Typography, { variant: "h3", className: styles.allOrgsSubHeading, noMargin: true }, t(TranslationsKeys.PROVISIONING_FORM_TITLE)),
        React.createElement(Vertical, { className: styles.allOrgsFieldGroup },
            React.createElement(Typography, { variant: "body-normal", className: styles.allOrgsFieldLabel, noMargin: true }, t(TranslationsKeys.PROVISIONING_BASE_URL)),
            React.createElement(Typography, { variant: "caption", className: styles.allOrgsFieldHint, noMargin: true }, t(TranslationsKeys.SETTINGS_SCIM_BASEURL_INFO)),
            React.createElement(Horizontal, { className: styles.allOrgsUrlRow },
                React.createElement(Input.Text, { value: scimBaseUrl, readOnly: true, ariaLabel: t(TranslationsKeys.PROVISIONING_BASE_URL), className: styles.allOrgsUrlInput }),
                React.createElement(Button, { text: urlCopied
                        ? t(TranslationsKeys.SETTINGS_COPIED)
                        : t(TranslationsKeys.SETTINGS_SCIM_PROVISIONING_COPY_BUTTON), type: "tertiary", onClick: handleCopyUrl, className: urlCopied
                        ? styles.copiedAllOrgsButtonWrapper
                        : styles.copyAllOrgsUrlButton, "data-testid": "settings-copy-url" }))),
        React.createElement(Vertical, { className: styles.allOrgsTokenSection },
            React.createElement(Vertical, { className: styles.allOrgsFieldLabelGroup },
                React.createElement(Typography, { variant: "body-normal", className: styles.allOrgsFieldLabel, noMargin: true }, t(TranslationsKeys.SETTINGS_PROVISIONING_ACTION_TITLE)),
                React.createElement(Typography, { variant: "caption", className: styles.allOrgsFieldHint, noMargin: true }, t(tokenUrl
                    ? TranslationsKeys.SETTINGS_SCIM_GENERATE_TOKEN_PRESENTED_INFO
                    : TranslationsKeys.SETTINGS_SCIM_GENERATE_TOKEN_INFO))),
            showRegenerateError && (React.createElement(Alert.Banner, { message: errorMessage ||
                    t(TranslationsKeys.SETTINGS_SCIM_ERROR_DEFAULT), isVisible: true, type: BannerAlertType.ERROR, showCloseButton: false, className: styles.allOrgsWarningBanner })),
            tokenUrl && (React.createElement(Horizontal, { className: styles.allOrgsUrlRow },
                React.createElement(Input.Text, { value: tokenUrl, readOnly: true, ariaLabel: t(TranslationsKeys.PROVISIOINING_ACTION_TITLE), className: styles.allOrgsUrlInput }),
                React.createElement(Button, { text: tokenCopied
                        ? t(TranslationsKeys.SETTINGS_COPIED)
                        : t(TranslationsKeys.SETTINGS_SCIM_PROVISIONING_COPY_BUTTON), type: "tertiary", onClick: handleCopyToken, className: tokenCopied
                        ? styles.copiedAllOrgsButtonWrapper
                        : styles.copyAllOrgsUrlButton, "data-testid": "settings-copy-token" }))),
            React.createElement(Button, { text: t(TranslationsKeys.PROVISIOINING_GENERATE_BUTTON), type: "secondary", onClick: handleGenerateToken, className: styles.allOrgsGenerateTokenButton, "data-testid": "settings-generate-token" }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { isOpen: true, onDismiss: onClose, size: ModalSize.MEDIUM3, title: t(TranslationsKeys.SETTINGS_MODAL_TITLE), className: styles.allOrgsModal, confirmButton: {
                text: t('CLOSE'),
                onConfirm: handleSave,
            }, isContentLoading: loading || getScimTokenLoading || isLoading, "data-testid": "all-orgs-settings-modal" },
            React.createElement(Horizontal, { className: styles.allOrgsModalBody },
                React.createElement(Vertical, { className: styles.allOrgsSidebar }, SIDEBAR_TABS.map(({ tab, translationKey, testId }) => {
                    const isActive = activeTab === tab;
                    return (React.createElement("div", { key: tab, className: isActive
                            ? styles.allOrgsSidebarItemBoxShadow
                            : undefined },
                        React.createElement("div", { role: "button", tabIndex: 0, "data-testid": testId, className: `${styles.allOrgsSidebarItem} ${isActive
                                ? styles.allOrgsSidebarItemActive
                                : ''}`, onClick: () => setActiveTab(tab), onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    setActiveTab(tab);
                                }
                            } }, t(translationKey))));
                })),
                activeTab === 'scim' && renderScimContent(),
                activeTab === 'localAuth' && (React.createElement(Vertical, { className: styles.allOrgsContent },
                    React.createElement(Alert.Banner, { message: t(TranslationsKeys.INTERNAL_AUTH_FORM_CONTENT), isVisible: true, type: BannerAlertType.WARNING, showCloseButton: false, className: styles.allOrgsWarningBanner }),
                    React.createElement(LocalAuthContent, { view: "localAuth", onSettingChanged: () => setSettingsChanged(true) }))),
                activeTab === 'mfaSettings' && (React.createElement(Vertical, { className: styles.allOrgsContent },
                    React.createElement(LocalAuthContent, { view: "mfaSettings", onSettingChanged: () => setSettingsChanged(true) }))))),
        React.createElement(GlobalAlert.Toast, { "data-testid": "all-orgs-settings-toast", message: toastMessage, isVisible: isToastVisible, onClose: () => setIsToastVisible(false), autoHide: true })));
};
//# sourceMappingURL=all-orgs-settings-modal.container.js.map